import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AssessmentModal } from './components/assessment-modal'
import { AssessmentPagination } from './components/assessment-pagination'
import { AssessmentProgress } from './components/assessment-progress'
import { AssessmentQuestion } from './components/assessment-question'
import { AssessmentSidebar } from './components/assessment-sidebar'
import { Menu } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { useAuth } from '@/context/AuthContext'
import { getUserDisplayName, getUserInitials } from '@/utils/user-display'
import { assessmentApi, learningApi } from '@/utils/api'
import type { RequiredVideosResponse } from '@/utils/api'
import { generateAssessmentAnalysis, getCategoryName } from '@/utils/assessment-analysis'
import { resolveBackendUrl, isExternalVideo, getEmbedUrl, getExternalThumbnail } from '@/utils/media'
import { useExternalVideoProgress } from '@/hooks/useExternalVideoProgress'
import { toast } from 'sonner'

type QuestionOption = { optionText: string; optionValue?: number; orderIndex?: number }
type Question = {
  id: string
  questionText: string
  sectionNumber: number
  sectionName?: string
  questionType?: string
  options?: QuestionOption[]
}
type AssessmentRecord = {
  id: string
  currentSection?: number
  totalSections?: number
  status?: string
  progressPercentage?: number
  scores?: Record<string, number>
  riskLevels?: Record<string, string>
}

export default function AssessmentPage() {
  const { user } = useAuth()
  const displayName = getUserDisplayName(user)
  const initials = getUserInitials(user)
  const coupleId = (user as { coupleId?: string })?.coupleId

  const [assessment, setAssessment] = useState<AssessmentRecord | null>(null)
  const [questions, setQuestions] = useState<Question[]>([])
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [modalType, setModalType] = useState<'review' | 'great-work' | 'made-start'>('review')
  const [showScoreAnalysis, setShowScoreAnalysis] = useState(false)
  const [latestScores, setLatestScores] = useState<{ scores?: Record<string, number>; riskLevels?: Record<string, string> } | null>(null)
  const [viewMode, setViewMode] = useState<'questions' | 'review'>('questions')
  const [reviewData, setReviewData] = useState<{
    questions: Question[]
    answers: Record<string, string>
    scores?: Record<string, number>
    riskLevels?: Record<string, string>
  } | null>(null)
  const [reviewTab, setReviewTab] = useState<'overall' | number>('overall')
  const [requiredVideos, setRequiredVideos] = useState<RequiredVideosResponse | null>(null)
  const [showRequiredVideos, setShowRequiredVideos] = useState(false)
  const [activeVideoUrl, setActiveVideoUrl] = useState<string | null>(null)
  const [activeVideoId, setActiveVideoId] = useState<string | null>(null)
  const [activeModuleId, setActiveModuleId] = useState<string | null>(null)
  const assessmentVideoRef = useRef<HTMLVideoElement>(null)
  const assessmentProgressInterval = useRef<ReturnType<typeof setInterval> | null>(null)
  const assessmentLastSentTime = useRef(0)
  const pendingSeekRef = useRef<number | null>(null)
  const extLastSentTime = useRef(0)
  const [assessmentWatchPct, setAssessmentWatchPct] = useState(0)
  const [assessmentWatchTime, setAssessmentWatchTime] = useState(0)
  const [assessmentTotalTime, setAssessmentTotalTime] = useState(0)

  const isActiveExternal = isExternalVideo(activeVideoUrl)

  // External video progress callback for assessment required videos
  const handleExtAssessmentProgress = useCallback(async (ws: number, td: number) => {
    if (!activeVideoId || !coupleId || !assessment?.id || td <= 0 || ws === extLastSentTime.current) return
    extLastSentTime.current = ws
    try {
      const res = await learningApi.updateVideoProgress(activeVideoId, coupleId, { watchedSeconds: ws, totalDuration: td })
      const data = (res as { data?: { isCompleted?: boolean; watchPercentage?: number } }).data
        || (res as unknown as { isCompleted?: boolean; watchPercentage?: number })
      if (data?.isCompleted) {
        setRequiredVideos(prev => {
          if (!prev) return prev
          return {
            ...prev,
            modules: prev.modules.map(m => ({
              ...m,
              videos: m.videos.map(v =>
                v.videoId === activeVideoId ? { ...v, isWatched: true, watchPercentage: 100 } : v
              )
            }))
          }
        })
        toast.success('Video completed!')
      } else if (data?.watchPercentage !== undefined) {
        setRequiredVideos(prev => {
          if (!prev) return prev
          return {
            ...prev,
            modules: prev.modules.map(m => ({
              ...m,
              videos: m.videos.map(v =>
                v.videoId === activeVideoId
                  ? { ...v, watchedSeconds: ws, watchPercentage: data.watchPercentage ?? 0 }
                  : v
              )
            }))
          }
        })
      }
    } catch { /* retry next interval */ }
  }, [activeVideoId, coupleId, assessment?.id])

  const {
    currentTime: extAssessmentCurrentTime,
    duration: extAssessmentDuration,
    percentage: extAssessmentPercentage,
    iframeRef: extAssessmentIframeRef,
    seekTo: extAssessmentSeekTo,
  } = useExternalVideoProgress({
    videoUrl: isActiveExternal ? activeVideoUrl : null,
    onProgress: handleExtAssessmentProgress,
  })

  // Reset progress tracking state when switching videos
  useEffect(() => {
    extLastSentTime.current = 0
    setAssessmentWatchPct(0)
    setAssessmentWatchTime(0)
    setAssessmentTotalTime(0)
  }, [activeVideoId])

  // Sync external video progress to local state
  useEffect(() => {
    if (!isActiveExternal) return
    setAssessmentWatchTime(extAssessmentCurrentTime)
    setAssessmentTotalTime(extAssessmentDuration)
    setAssessmentWatchPct(extAssessmentPercentage)
  }, [isActiveExternal, extAssessmentCurrentTime, extAssessmentDuration, extAssessmentPercentage])

  const currentSection = assessment?.currentSection || 1
  const totalSections = assessment?.totalSections || 1
  const progressPct = assessment?.progressPercentage || 0

  const sections = useMemo(() => {
    const maxSection =
      questions.reduce((max, q) => Math.max(max, q.sectionNumber || 0), totalSections) || totalSections
    return Array.from({ length: maxSection }, (_, i) => ({
      id: `section-${i + 1}`,
      label: `Section ${i + 1}`,
      isCompleted: (assessment?.currentSection || 1) > i + 1 || assessment?.status === 'completed',
    }))
  }, [questions, totalSections, assessment?.currentSection, assessment?.status])

  const sectionQuestions = useMemo(
    () => questions.filter((q) => q.sectionNumber === currentSection),
    [questions, currentSection]
  )

  const allVideosCompleted = useMemo(() => {
    if (!requiredVideos?.required || !requiredVideos.modules.length) return true
    return requiredVideos.modules.every(m => m.videos.every(v => v.isWatched))
  }, [requiredVideos])

  useEffect(() => {
    const load = async () => {
      if (!coupleId) {
        setError('Missing couple id.')
        setLoading(false)
        return
      }
      setLoading(true)
      setError(null)
      try {
        const startRes = await assessmentApi.startAssessment(coupleId)
        const assessmentData =
          (startRes as { data?: AssessmentRecord }).data || (startRes as unknown as AssessmentRecord)
        setAssessment(assessmentData)

        const questionsRes = await assessmentApi.listQuestions()
        const qs = (questionsRes as { data?: Question[] }).data || (questionsRes as unknown as Question[])
        setQuestions(Array.isArray(qs) ? qs : [])

        // Check for pending required videos (survives page refresh)
        if (assessmentData.status !== 'completed' && assessmentData.id) {
          try {
            const videosRes = await assessmentApi.getRequiredVideos(assessmentData.id)
            const videosData = (videosRes as { data?: RequiredVideosResponse }).data
              || (videosRes as unknown as RequiredVideosResponse)
            if (videosData?.required && !videosData.allCompleted) {
              setRequiredVideos(videosData)
              setShowRequiredVideos(true)
            }
          } catch {
            // No pending videos — continue normally
          }
        }
      } catch (err) {
        setError((err as Error)?.message || 'Unable to load assessment.')
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [coupleId])

  const handleSelect = (questionId: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }))
  }

  const handleSubmitSection = async () => {
    if (!assessment?.id) return
    const answeredCount = sectionQuestions.filter((q) => answers[q.id]).length
    const minimumRequired = Math.ceil(sectionQuestions.length / 2)
    if (answeredCount < minimumRequired) {
      toast.warning(`Please answer at least ${minimumRequired} of ${sectionQuestions.length} questions to continue.`)
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const payload = sectionQuestions.map((q) => {
        const ans = answers[q.id] || ''
        const score = q.options?.find((o) => o.optionText === ans)?.optionValue
        return {
          questionId: q.id,
          sectionNumber: q.sectionNumber,
          answer: ans,
          ...(score !== undefined && { score })
        }
      })
      const res = await assessmentApi.submitSection(assessment.id, payload)
      const next = (res as { data?: AssessmentRecord }).data || (res as unknown as AssessmentRecord)
      setAssessment(next)

      if (next.scores && next.riskLevels) {
        setLatestScores({ scores: next.scores, riskLevels: next.riskLevels })
        setShowScoreAnalysis(true)

        // Fetch required videos for this section
        try {
          const videosRes = await assessmentApi.getRequiredVideos(assessment.id)
          const videosData = (videosRes as { data?: RequiredVideosResponse }).data
            || (videosRes as unknown as RequiredVideosResponse)
          setRequiredVideos(videosData)
        } catch {
          setRequiredVideos({ required: false, modules: [] })
        }
      }

      if (next.status === 'completed') {
        setModalType('great-work')
        setShowModal(true)
      } else {
        setShowModal(false)
        setModalType('review')
      }
    } catch (err) {
      const apiErr = err as { status?: number; response?: { incompleteModules?: unknown[] } }
      if (apiErr.status === 400 && apiErr.response?.incompleteModules) {
        toast.error('Please complete all required videos before proceeding to the next section.')
        try {
          const videosRes = await assessmentApi.getRequiredVideos(assessment.id)
          const videosData = (videosRes as { data?: RequiredVideosResponse }).data
            || (videosRes as unknown as RequiredVideosResponse)
          setRequiredVideos(videosData)
          setShowRequiredVideos(true)
        } catch {
          // fallback
        }
      } else {
        toast.error((err as Error)?.message || 'Unable to submit section.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleContinueToNextSection = () => {
    setShowScoreAnalysis(false)
    setShowRequiredVideos(false)
    setRequiredVideos(null)
    setActiveVideoUrl(null)
    setActiveVideoId(null)
    setActiveModuleId(null)
    setLatestScores(null)
    setAnswers({})
  }

  const handleTransitionToVideos = () => {
    setShowScoreAnalysis(false)
    setShowRequiredVideos(true)
  }

  const sendAssessmentVideoProgress = useCallback(async () => {
    const vid = assessmentVideoRef.current
    if (!vid || !activeVideoId || !coupleId || !assessment?.id) return
    const watchedSeconds = Math.floor(vid.currentTime)
    const totalDuration = Math.floor(vid.duration) || 0
    if (totalDuration <= 0 || watchedSeconds === assessmentLastSentTime.current) return
    assessmentLastSentTime.current = watchedSeconds
    try {
      const res = await learningApi.updateVideoProgress(activeVideoId, coupleId, { watchedSeconds, totalDuration })
      const data = (res as { data?: { isCompleted?: boolean; watchPercentage?: number } }).data
        || (res as unknown as { isCompleted?: boolean; watchPercentage?: number })
      if (data?.isCompleted) {
        setRequiredVideos(prev => {
          if (!prev) return prev
          return {
            ...prev,
            modules: prev.modules.map(m => ({
              ...m,
              videos: m.videos.map(v =>
                v.videoId === activeVideoId ? { ...v, isWatched: true, watchPercentage: 100 } : v
              )
            }))
          }
        })
        toast.success('Video completed!')
      } else if (data?.watchPercentage !== undefined) {
        setRequiredVideos(prev => {
          if (!prev) return prev
          return {
            ...prev,
            modules: prev.modules.map(m => ({
              ...m,
              videos: m.videos.map(v =>
                v.videoId === activeVideoId
                  ? { ...v, watchedSeconds, watchPercentage: data.watchPercentage ?? 0 }
                  : v
              )
            }))
          }
        })
      }
    } catch {
      // Silently fail — retries on next interval
    }
  }, [activeVideoId, coupleId, assessment?.id])

  // Auto-track progress for assessment inline player
  useEffect(() => {
    const vid = assessmentVideoRef.current
    if (!vid) return

    const onPlay = () => {
      if (assessmentProgressInterval.current) clearInterval(assessmentProgressInterval.current)
      assessmentProgressInterval.current = setInterval(sendAssessmentVideoProgress, 7_000)
    }
    const onPause = () => {
      sendAssessmentVideoProgress()
      if (assessmentProgressInterval.current) { clearInterval(assessmentProgressInterval.current); assessmentProgressInterval.current = null }
    }
    const onEnded = () => {
      sendAssessmentVideoProgress()
      if (assessmentProgressInterval.current) { clearInterval(assessmentProgressInterval.current); assessmentProgressInterval.current = null }
    }

    const onTimeUpdate = () => {
      if (!vid.duration) return
      setAssessmentWatchTime(Math.floor(vid.currentTime))
      setAssessmentTotalTime(Math.floor(vid.duration))
      setAssessmentWatchPct((vid.currentTime / vid.duration) * 100)
    }

    vid.addEventListener('play', onPlay)
    vid.addEventListener('pause', onPause)
    vid.addEventListener('ended', onEnded)
    vid.addEventListener('timeupdate', onTimeUpdate)

    return () => {
      vid.removeEventListener('play', onPlay)
      vid.removeEventListener('pause', onPause)
      vid.removeEventListener('ended', onEnded)
      vid.removeEventListener('timeupdate', onTimeUpdate)
      if (assessmentProgressInterval.current) clearInterval(assessmentProgressInterval.current)
    }
  }, [sendAssessmentVideoProgress])

  // Reset progress tracking state when switching videos
  useEffect(() => {
    assessmentLastSentTime.current = 0
  }, [activeVideoId])

  // Apply pending seek when video element becomes available
  // (pendingSeekRef is set by the async onClick handler after fetch completes)
  useEffect(() => {
    if (!activeVideoId || isActiveExternal) return
    const checkPendingSeek = setInterval(() => {
      if (pendingSeekRef.current === null) return
      const vid = assessmentVideoRef.current
      if (!vid) return
      const seekTarget = pendingSeekRef.current
      if (vid.readyState >= 1) {
        vid.currentTime = seekTarget
        pendingSeekRef.current = null
        clearInterval(checkPendingSeek)
      } else {
        const onLoaded = () => {
          vid.currentTime = seekTarget
          pendingSeekRef.current = null
          clearInterval(checkPendingSeek)
        }
        vid.addEventListener('loadedmetadata', onLoaded, { once: true })
        clearInterval(checkPendingSeek)
      }
    }, 200)
    return () => clearInterval(checkPendingSeek)
  }, [activeVideoId, isActiveExternal])

  const handleViewReview = async () => {
    if (!assessment?.id) return
    try {
      const response = await assessmentApi.getAssessment(assessment.id)
      const assessmentData = (response as { data?: any }).data || response
      const answerMap: Record<string, string> = {}
      if (assessmentData.responses && Array.isArray(assessmentData.responses)) {
        assessmentData.responses.forEach((resp: any) => {
          if (resp.questionId && resp.answer) {
            answerMap[resp.questionId] = resp.answer
          }
        })
      }
      setReviewData({
        questions,
        answers: answerMap,
        scores: assessmentData.scores || assessment.scores || {},
        riskLevels: assessmentData.riskLevels || assessment.riskLevels || {}
      })
      setReviewTab('overall')
      setViewMode('review')
    } catch (err) {
      console.error('Failed to load review data:', err)
    }
  }

  const handleBackToQuestions = () => {
    setViewMode('questions')
    setReviewData(null)
  }

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  const activeVideoCompleted = requiredVideos?.modules
    .flatMap(m => m.videos)
    .find(v => v.videoId === activeVideoId)?.isWatched ?? false

  return (
    <div className='min-h-screen w-full bg-[#EDEFF3]'>
      <div className='p-[27px]'>
        <div className='bg-white rounded-[24px] min-h-[calc(100vh-54px)]'>
          {/* Header */}
          <div className='px-8 py-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4'>
            <div className='flex items-center gap-4'>
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant='ghost' size='icon' className='lg:hidden -ml-2'>
                    <Menu className='h-6 w-6' />
                    <span className='sr-only'>Toggle menu</span>
                  </Button>
                </SheetTrigger>
                <SheetContent side='left' className='w-[280px] p-4'>
                  <AssessmentSidebar
                    sections={sections}
                    activeSection={`section-${currentSection}`}
                    sectionsCompleted={sections.filter((s) => s.isCompleted).length}
                    totalSections={sections.length}
                    progressPercentage={progressPct}
                    onSectionClick={() => {}}
                  />
                </SheetContent>
              </Sheet>
              <h1 className='text-[40px] font-bold text-[#151515] leading-[70px]'>Assessment</h1>
              {assessment?.status === 'completed' && (
                <Button
                  onClick={viewMode === 'review' ? handleBackToQuestions : handleViewReview}
                  variant='outline'
                  className='text-sm'
                >
                  {viewMode === 'review' ? 'Back to Assessment' : 'Review Answers'}
                </Button>
              )}
            </div>
            <div className='flex items-center gap-4 lg:gap-8'>
              <div className='hidden md:flex bg-[#F7F8FA] rounded-[12px] px-[10px] py-[10px]'>
                <p className='text-[12px] font-normal text-[#747474] max-w-[522px]'>
                  This assessment helps you discover your strengths and areas where you can grow. There are no right or wrong answers —
                  <span className='font-bold'> just be honest</span>
                </p>
              </div>
              <div className='flex items-center gap-3'>
                <div className='w-[48px] h-[47px] bg-[#151515] rounded-full flex items-center justify-center'>
                  <span className='text-[16px] font-bold text-white'>{initials}</span>
                </div>
                <span className='text-[24px] font-bold text-[#151515] hidden sm:block'>{displayName}</span>
              </div>
              <div className='w-[41px] h-[39px] bg-[#F7F8FA] rounded-full flex items-center justify-center cursor-pointer'>
                <svg width='4' height='18' viewBox='0 0 4 18' fill='#434343'>
                  <circle cx='2' cy='2' r='2' />
                  <circle cx='2' cy='9' r='2' />
                  <circle cx='2' cy='16' r='2' />
                </svg>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className='flex flex-row h-[calc(100vh-180px)]'>
            {/* Left Sidebar */}
            <div className='hidden lg:flex flex-col w-[280px] px-[20px] pb-4'>
              <AssessmentSidebar
                sections={sections}
                activeSection={`section-${currentSection}`}
                sectionsCompleted={sections.filter((s) => s.isCompleted).length}
                totalSections={sections.length}
                progressPercentage={progressPct}
                onSectionClick={() => {}}
              />
            </div>
            {/* Main Content */}
            <div className='flex-1 flex flex-col'>
              <div className='flex-1 overflow-y-auto px-4 md:px-8 py-6'>
                {loading ? (
                  <div className='flex items-center justify-center h-full'>
                    <p className='text-[#4e4d4d] text-[16px]'>Loading assessment...</p>
                  </div>
                ) : error ? (
                  <div className='flex items-center justify-center h-full'>
                    <p className='text-red-600 text-[16px]'>{error}</p>
                  </div>
                ) : assessment?.status === 'completed' ? (
                  viewMode === 'review' && reviewData ? (
                    <div className='max-w-6xl mx-auto'>
                      <div className='mb-8 text-center'>
                        <h2 className='mb-3 text-2xl md:text-3xl font-bold text-gray-900'>Assessment Review</h2>
                        <p className='text-sm md:text-base text-gray-600'>Here are all your questions and the answers you selected.</p>
                      </div>
                      <div className='mb-6 bg-white rounded-xl border border-gray-200 p-2'>
                        <div className='flex flex-wrap gap-2'>
                          <button
                            onClick={() => setReviewTab('overall')}
                            className={`px-4 py-2 rounded-lg font-semibold text-sm ${reviewTab === 'overall' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                          >
                            Overall Analysis
                          </button>
                          {Array.from(new Set(reviewData.questions.map(q => q.sectionNumber))).sort((a, b) => a - b).map(sectionNum => (
                            <button
                              key={sectionNum}
                              onClick={() => setReviewTab(sectionNum)}
                              className={`px-4 py-2 rounded-lg font-semibold text-sm ${reviewTab === sectionNum ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                            >
                              {reviewData.questions.find(q => q.sectionNumber === sectionNum)?.sectionName || `Section ${sectionNum}`}
                            </button>
                          ))}
                        </div>
                      </div>
                      {reviewTab === 'overall' ? (
                        reviewData.scores && Object.keys(reviewData.scores).length > 0 && (
                          <div className='mb-8 bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-6 border border-orange-200'>
                            <h3 className='text-lg font-bold text-gray-900 mb-4'>Overall Analysis</h3>
                            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                              {Object.entries(reviewData.scores).map(([category]) => {
                                const riskLevel = reviewData.riskLevels?.[category]?.toLowerCase() || 'low'
                                const borderColor = riskLevel === 'high' ? 'border-l-red-500' : riskLevel === 'moderate' ? 'border-l-amber-400' : 'border-l-emerald-500'
                                const badgeBg = riskLevel === 'high' ? 'bg-red-50 text-red-700' : riskLevel === 'moderate' ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'
                                const dotColor = riskLevel === 'high' ? 'bg-red-500' : riskLevel === 'moderate' ? 'bg-amber-400' : 'bg-emerald-500'
                                const barWidth = riskLevel === 'high' ? 'w-full' : riskLevel === 'moderate' ? 'w-3/5' : 'w-1/4'
                                const barColor = riskLevel === 'high' ? 'bg-red-400' : riskLevel === 'moderate' ? 'bg-amber-300' : 'bg-emerald-400'
                                return (
                                  <div key={category} className={`bg-white rounded-xl border border-gray-100 border-l-4 ${borderColor} p-4`}>
                                    <div className='flex items-center justify-between mb-3'>
                                      <span className='text-sm font-semibold text-gray-900 capitalize'>{category.replace(/_/g, ' ')}</span>
                                      <span className={`flex items-center gap-1.5 px-2.5 py-0.5 text-[11px] font-bold rounded-full ${badgeBg}`}>
                                        <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
                                        {riskLevel.toUpperCase()}
                                      </span>
                                    </div>
                                    <div className='h-2 w-full rounded-full bg-gray-100 overflow-hidden'>
                                      <div className={`h-full rounded-full ${barColor} ${barWidth} transition-all duration-700`} />
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        )
                      ) : (
                        (() => {
                          const sectionNum = reviewTab as number
                          const sectionQs = reviewData.questions.filter(q => q.sectionNumber === sectionNum)
                          return (
                            <div className='mb-8'>
                              <h3 className='text-xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-gray-200'>
                                {sectionQs[0]?.sectionName || `Section ${sectionNum}`}
                              </h3>
                              <div className='space-y-6'>
                                {sectionQs.map((q, idx) => {
                                  const userAnswer = reviewData.answers[q.id]
                                  return (
                                    <div key={q.id} className='bg-white rounded-xl border border-gray-200 p-6'>
                                      <div className='mb-4'>
                                        <span className='text-sm font-semibold text-orange-600'>Question {idx + 1}</span>
                                        <p className='mt-2 text-base font-medium text-gray-900'>{q.questionText}</p>
                                      </div>
                                      {q.options && q.options.length > 0 ? (
                                        <div className='space-y-2'>
                                          {q.options.map((opt) => {
                                            const isSelected = opt.optionText === userAnswer
                                            return (
                                              <div key={opt.optionText} className={`p-3 rounded-lg border-2 ${isSelected ? 'border-orange-500 bg-orange-50' : 'border-gray-200 bg-gray-50'}`}>
                                                <div className='flex items-center gap-3'>
                                                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isSelected ? 'border-orange-500 bg-orange-500' : 'border-gray-300'}`}>
                                                    {isSelected && <div className='w-2 h-2 rounded-full bg-white' />}
                                                  </div>
                                                  <span className={`text-sm ${isSelected ? 'text-orange-900 font-semibold' : 'text-gray-700'}`}>{opt.optionText}</span>
                                                </div>
                                              </div>
                                            )
                                          })}
                                        </div>
                                      ) : userAnswer ? (
                                        <div className='p-3 rounded-lg border-2 border-orange-500 bg-orange-50'>
                                          <p className='text-sm text-orange-900 font-semibold'>Your answer: {userAnswer}</p>
                                        </div>
                                      ) : (
                                        <p className='text-sm text-gray-500 italic'>No answer recorded</p>
                                      )}
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          )
                        })()
                      )}
                    </div>
                  ) : assessment.scores && Object.keys(assessment.scores).length > 0 ? (
                    <div className='max-w-4xl mx-auto'>
                      <div className='mb-8 text-center'>
                        <h2 className='mb-3 text-2xl md:text-3xl font-bold text-gray-900'>Assessment Completed</h2>
                        <div className='mb-6'>
                          <div className='h-2 w-full overflow-hidden rounded-full bg-gray-200'>
                            <div className='h-full rounded-full bg-green-500' style={{ width: '100%' }} />
                          </div>
                          <div className='mt-2 flex items-center justify-between text-sm'>
                            <span className='font-medium text-green-600'>Completed</span>
                            <span className='font-semibold text-gray-900'>100%</span>
                          </div>
                        </div>
                      </div>
                      {(() => {
                        const analysis = generateAssessmentAnalysis(assessment.scores || {}, assessment.riskLevels || {})
                        const overallRisk = assessment.riskLevels?.overall?.toLowerCase() || 'low'
                        const overallGradient = overallRisk === 'high'
                          ? 'from-red-500 to-orange-500'
                          : overallRisk === 'moderate'
                          ? 'from-amber-400 to-orange-500'
                          : 'from-emerald-400 to-teal-500'
                        const overallIcon = overallRisk === 'high'
                          ? 'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z'
                          : overallRisk === 'moderate'
                          ? 'M12 9v3.75m0-10.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z'
                          : 'M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z'
                        return (
                          <>
                            {/* Overall Assessment Card */}
                            <div className={`mb-8 rounded-3xl overflow-hidden shadow-lg`}>
                              <div className={`bg-gradient-to-r ${overallGradient} px-8 py-10 text-white text-center`}>
                                <div className='flex justify-center mb-4'>
                                  <div className='w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center'>
                                    <svg className='w-9 h-9 text-white' fill='none' viewBox='0 0 24 24' strokeWidth={1.5} stroke='currentColor'>
                                      <path strokeLinecap='round' strokeLinejoin='round' d={overallIcon} />
                                    </svg>
                                  </div>
                                </div>
                                <h3 className='text-2xl font-bold mb-1'>Overall Assessment</h3>
                                <span className='inline-block px-4 py-1 rounded-full bg-white/20 backdrop-blur-sm text-sm font-semibold tracking-wide mt-2'>
                                  {overallRisk.toUpperCase()} RISK
                                </span>
                              </div>
                              <div className='bg-white px-8 py-6'>
                                <p className='text-sm md:text-base text-gray-600 leading-relaxed text-center'>{analysis.overallSummary}</p>
                              </div>
                            </div>

                            {/* Category Breakdown */}
                            {Object.keys(assessment.scores || {}).filter(k => k !== 'overall').length > 0 && (
                              <div className='mb-8'>
                                <h3 className='text-lg font-bold text-gray-900 mb-5'>Category Breakdown</h3>
                                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                                  {Object.entries(assessment.scores || {}).filter(([category]) => category !== 'overall').map(([category]) => {
                                    const riskLevel = assessment.riskLevels?.[category]?.toLowerCase() || 'low'
                                    const borderColor = riskLevel === 'high' ? 'border-l-red-500' : riskLevel === 'moderate' ? 'border-l-amber-400' : 'border-l-emerald-500'
                                    const badgeBg = riskLevel === 'high' ? 'bg-red-50 text-red-700' : riskLevel === 'moderate' ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'
                                    const dotColor = riskLevel === 'high' ? 'bg-red-500' : riskLevel === 'moderate' ? 'bg-amber-400' : 'bg-emerald-500'
                                    const barWidth = riskLevel === 'high' ? 'w-full' : riskLevel === 'moderate' ? 'w-3/5' : 'w-1/4'
                                    const barColor = riskLevel === 'high' ? 'bg-red-400' : riskLevel === 'moderate' ? 'bg-amber-300' : 'bg-emerald-400'
                                    return (
                                      <div key={category} className={`bg-white rounded-xl border border-gray-100 border-l-4 ${borderColor} p-5 shadow-sm hover:shadow-md transition-shadow`}>
                                        <div className='flex items-start justify-between mb-3'>
                                          <span className='text-[15px] font-semibold text-gray-900'>{getCategoryName(category)}</span>
                                          <span className={`flex items-center gap-1.5 px-2.5 py-0.5 text-[11px] font-bold rounded-full ${badgeBg}`}>
                                            <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
                                            {riskLevel.toUpperCase()}
                                          </span>
                                        </div>
                                        <div className='h-2 w-full rounded-full bg-gray-100 overflow-hidden'>
                                          <div className={`h-full rounded-full ${barColor} ${barWidth} transition-all duration-700`} />
                                        </div>
                                      </div>
                                    )
                                  })}
                                </div>
                              </div>
                            )}

                            {/* Recommended Next Steps */}
                            {analysis.recommendations.length > 0 && (
                              <div className='mb-6 bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-6 border border-orange-100'>
                                <h3 className='text-lg font-bold text-gray-900 mb-4'>Recommended Next Steps</h3>
                                <ul className='space-y-3'>
                                  {analysis.recommendations.map((rec: string, index: number) => (
                                    <li key={index} className='flex items-start gap-3'>
                                      <span className='flex-shrink-0 w-7 h-7 rounded-full bg-orange-500 text-white flex items-center justify-center text-xs font-bold shadow-sm'>{index + 1}</span>
                                      <span className='text-sm md:text-base text-gray-700 leading-relaxed'>{rec}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </>
                        )
                      })()}
                    </div>
                  ) : (
                    <AssessmentProgress title='Assessment Completed' progress={100} isComplete />
                  )
                ) : showScoreAnalysis && latestScores ? (
                  <div className='flex flex-col gap-[4px]'>
                    <div className='flex flex-col gap-[4px] mb-6'>
                      <h2 className='text-[20px] font-bold text-[#151515] leading-[32px]'>
                        Section {currentSection - 1} Complete!
                      </h2>
                      <p className='text-[16px] font-normal text-[#151515] leading-[22px]'>
                        Here's your analysis for this section. Review your results and continue when ready.
                      </p>
                    </div>

                    <div className='flex flex-col gap-[19px] mb-8'>
                      {Object.entries(latestScores.scores || {}).filter(([key]) => key !== 'overall').map(([category]) => {
                        const riskLevel = latestScores.riskLevels?.[category]
                        const normalized = riskLevel?.toLowerCase() || 'low'
                        const riskCategory: 'low' | 'average' | 'high' =
                          (normalized === 'low' || normalized === 'no risk') ? 'low' :
                          normalized === 'moderate' ? 'average' : 'high'
                        const categoryName = getCategoryName(category)

                        const recommendation = (() => {
                          switch (riskCategory) {
                            case 'low':
                              return {
                                title: 'Looking good!',
                                message: `You scored low in this category: this indicates that there is a low tendency for violence as it relates to ${categoryName}. You would be moving to the next phase! Congratulations.`,
                                showLink: false,
                              }
                            case 'average':
                              return {
                                title: 'Fair enough...',
                                message: `Your scores came out as average in this category; however, we are leaving nothing to chance — we would be referring you to a correctional video to add colour to any grey areas!`,
                                showLink: true,
                                linkText: 'Kindly click the link below to get access to the correctional modules.',
                              }
                            case 'high':
                              return {
                                title: 'Ooops!',
                                message: `You scored high in this category: there is tendency of violence and lots of room for improvement. This is the whole essence of putting together IRIS to rid your marriage off any form of violence. Based on your score, we would be directing you to a series of correctional videos.`,
                                showLink: true,
                                linkText: 'Kindly click here to access them.',
                              }
                          }
                        })()

                        const riskStyle = riskCategory === 'low'
                          ? { color: '#0ECF5B', iconColor: '#0ECF5B' }
                          : riskCategory === 'average'
                          ? { color: '#FFB319', iconColor: '#FFB319' }
                          : { color: '#FF7043', iconColor: '#FF7043' }

                        return (
                          <div key={category} className='bg-[rgba(243,244,246,0.45)] rounded-[12px] p-[30px]'>
                            <h3 className='text-[18px] font-bold text-black leading-[28px] mb-4'>
                              {categoryName}
                            </h3>
                            <div className='flex items-center gap-2 mb-3'>
                              <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
                                <path
                                  d='M12 2C6.49 2 2 6.49 2 12C2 17.51 6.49 22 12 22C17.51 22 22 17.51 22 12C22 6.49 17.51 2 12 2ZM16.78 9.7L11.11 15.37C10.97 15.51 10.78 15.59 10.58 15.59C10.38 15.59 10.19 15.51 10.05 15.37L7.22 12.54C6.93 12.25 6.93 11.77 7.22 11.48C7.51 11.19 7.99 11.19 8.28 11.48L10.58 13.78L15.72 8.64C16.01 8.35 16.49 8.35 16.78 8.64C17.07 8.93 17.07 9.4 16.78 9.7Z'
                                  fill={riskStyle.iconColor}
                                />
                              </svg>
                              <span className='text-[16px] font-bold leading-[24px]' style={{ color: riskStyle.color }}>
                                {recommendation.title}
                              </span>
                            </div>
                            <p className='text-[14px] font-normal text-[#4e4d4d] leading-[22px] text-justify mb-4'>
                              {recommendation.message}
                            </p>
                            {recommendation.showLink && (
                              <p className='text-[14px] font-medium text-[#4e4d4d] leading-[22px]'>
                                {requiredVideos?.required
                                  ? 'You will be asked to watch correctional videos before continuing.'
                                  : recommendation.linkText}
                              </p>
                            )}
                          </div>
                        )
                      })}
                    </div>

                    <div className='flex justify-center'>
                      {requiredVideos?.required ? (
                        <Button onClick={handleTransitionToVideos} className='px-8 py-6 text-lg font-semibold bg-[#FA6017] hover:bg-[#e55109] text-white rounded-xl'>
                          Watch Required Videos to Continue
                        </Button>
                      ) : (
                        <Button onClick={handleContinueToNextSection} className='px-8 py-6 text-lg font-semibold bg-gray-900 hover:bg-gray-800 text-white rounded-xl'>
                          Continue to Next Section
                        </Button>
                      )}
                    </div>
                  </div>
                ) : showRequiredVideos && requiredVideos?.required ? (
                  <div className='flex flex-col'>
                    {/* Header */}
                    <div className='mb-6'>
                      <h2 className='text-[20px] font-bold text-[#151515] leading-[32px] mb-1'>
                        Required Videos
                      </h2>
                      <p className='text-[14px] font-normal text-[#747474] leading-[22px]'>
                        Watch all required videos before continuing to the next section.
                      </p>
                      {/* Progress bar */}
                      <div className='mt-4'>
                        <div className='flex items-center justify-between text-sm mb-2'>
                          <span className='text-[#4e4d4d]'>
                            <span className='font-semibold text-[#151515]'>
                              {requiredVideos.modules.reduce((acc, m) => acc + m.videos.filter(v => v.isWatched).length, 0)}
                            </span>
                            {' '}of{' '}
                            <span className='font-semibold text-[#151515]'>
                              {requiredVideos.modules.reduce((acc, m) => acc + m.videos.length, 0)}
                            </span>
                            {' '}completed
                          </span>
                          <span className='font-semibold text-[#151515]'>
                            {Math.round((requiredVideos.modules.reduce((acc, m) => acc + m.videos.filter(v => v.isWatched).length, 0) / Math.max(requiredVideos.modules.reduce((acc, m) => acc + m.videos.length, 0), 1)) * 100)}%
                          </span>
                        </div>
                        <div className='h-2 w-full overflow-hidden rounded-full bg-gray-200'>
                          <div
                            className='h-full rounded-full bg-[#FA6017] transition-all duration-500'
                            style={{
                              width: `${(requiredVideos.modules.reduce((acc, m) => acc + m.videos.filter(v => v.isWatched).length, 0) / Math.max(requiredVideos.modules.reduce((acc, m) => acc + m.videos.length, 0), 1)) * 100}%`
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Inline Video Player */}
                    {activeVideoUrl && (
                      <div className='mb-8 rounded-2xl overflow-hidden shadow-lg border border-gray-200'>
                        <div className='relative aspect-video bg-black'>
                          {isExternalVideo(activeVideoUrl) ? (
                            <iframe
                              ref={extAssessmentIframeRef}
                              key={activeVideoUrl}
                              className='absolute inset-0 h-full w-full'
                              src={getEmbedUrl(activeVideoUrl) || ''}
                              allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                              allowFullScreen
                              frameBorder='0'
                            />
                          ) : (
                            <video
                              ref={assessmentVideoRef}
                              key={activeVideoUrl}
                              className='absolute inset-0 h-full w-full object-contain'
                              controls
                              autoPlay
                              src={activeVideoUrl}
                            />
                          )}
                        </div>
                        <div className='bg-white p-4 flex items-center justify-between'>
                          <div>
                            <p className='text-[15px] font-semibold text-[#151515]'>
                              {requiredVideos.modules
                                .flatMap(m => m.videos)
                                .find(v => v.videoId === activeVideoId)?.title || 'Video'}
                            </p>
                            <p className='text-[13px] text-[#747474] mt-0.5'>
                              {requiredVideos.modules
                                .flatMap(m => m.videos)
                                .find(v => v.videoId === activeVideoId)?.description || ''}
                            </p>
                          </div>
                          {activeVideoCompleted ? (
                            <div className='flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-50 border border-green-200 shrink-0'>
                              <svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
                                <circle cx='12' cy='12' r='12' fill='#0ECF5B' />
                                <path d='M8 12l3 3 5-6' stroke='white' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                              </svg>
                              <span className='text-sm font-semibold text-green-700'>Completed</span>
                            </div>
                          ) : activeVideoId && activeModuleId ? (
                            <button
                              className='px-4 py-2 text-sm font-semibold text-white bg-[#FA6017] hover:bg-[#e55109] rounded-lg transition-colors shrink-0'
                              onClick={async () => {
                                if (!coupleId) return
                                try {
                                  await learningApi.completeVideo(activeModuleId, activeVideoId, coupleId)
                                  setRequiredVideos(prev => {
                                    if (!prev) return prev
                                    return {
                                      ...prev,
                                      modules: prev.modules.map(m => ({
                                        ...m,
                                        videos: m.videos.map(v =>
                                          v.videoId === activeVideoId ? { ...v, isWatched: true, watchPercentage: 100 } : v
                                        )
                                      }))
                                    }
                                  })
                                  toast.success('Video marked as complete!')
                                } catch {
                                  toast.error('Failed to mark video as complete.')
                                }
                              }}
                            >
                              Mark as Complete
                            </button>
                          ) : null}
                        </div>
                        {/* Watch Progress Indicator */}
                        <div className='bg-gray-50 px-4 py-3 border-t border-gray-100'>
                          <div className='flex items-center justify-between text-sm text-gray-600 mb-1.5'>
                            <span className='font-medium'>
                              {activeVideoCompleted ? 'Completed' : `${Math.round(assessmentWatchPct)}% watched`}
                            </span>
                            <span className='font-medium'>
                              {formatTime(assessmentWatchTime)} / {formatTime(assessmentTotalTime)}
                            </span>
                          </div>
                          <div className='h-1.5 w-full overflow-hidden rounded-full bg-gray-200'>
                            <div
                              className={`h-full rounded-full transition-all duration-300 ${
                                activeVideoCompleted ? 'bg-green-500' : 'bg-[#FA6017]'
                              }`}
                              style={{ width: `${activeVideoCompleted ? 100 : assessmentWatchPct}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Video Cards by Module */}
                    <div className='flex flex-col gap-8 mb-8'>
                      {requiredVideos.modules.map(module => (
                        <div key={module.moduleId}>
                          {module.title && (
                            <div className='flex items-center gap-2 mb-4'>
                              <svg className='h-5 w-5 text-[#151515]' fill='currentColor' viewBox='0 0 20 20'>
                                <path d='M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z' />
                              </svg>
                              <h3 className='text-[18px] font-bold text-[#151515]'>{module.title}</h3>
                            </div>
                          )}
                          <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'>
                            {module.videos.map((video, idx) => {
                              const videoSrc = video.videoUrl ? resolveBackendUrl(video.videoUrl) : ''
                              const thumbSrc = video.videoUrl ? getExternalThumbnail(video.videoUrl) : null
                              const isActive = activeVideoId === video.videoId
                              const formatDur = (s?: number) => {
                                if (!s) return '—'
                                return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`
                              }
                              return (
                                <div
                                  key={video.videoId}
                                  className={`group cursor-pointer rounded-[12px] transition-all ${
                                    isActive ? 'ring-2 ring-[#FA6017] ring-offset-2' : ''
                                  } ${video.isWatched ? 'opacity-75' : ''}`}
                                  onClick={async () => {
                                    if (videoSrc) {
                                      setActiveVideoUrl(videoSrc)
                                      setActiveVideoId(video.videoId)
                                      setActiveModuleId(module.moduleId)
                                      // Start module so progress can be tracked
                                      if (coupleId) {
                                        learningApi.startModule(module.moduleId, coupleId).catch(() => {})
                                      }
                                      // Resume from last position and restore visual progress
                                      if (assessment?.id && coupleId && !video.isWatched) {
                                        try {
                                          const res = await learningApi.getVideoProgress(
                                            video.videoId,
                                            coupleId
                                          )
                                          const data = (res as { data?: { watchedSeconds?: number; totalDuration?: number } }).data
                                            || (res as unknown as { watchedSeconds?: number; totalDuration?: number })
                                          if (data?.watchedSeconds && data.watchedSeconds > 0) {
                                            // Restore visual progress state
                                            setAssessmentWatchTime(data.watchedSeconds)
                                            if (data.totalDuration && data.totalDuration > 0) {
                                              setAssessmentTotalTime(data.totalDuration)
                                              setAssessmentWatchPct((data.watchedSeconds / data.totalDuration) * 100)
                                            } else if (video.watchPercentage && video.watchPercentage > 0) {
                                              setAssessmentWatchPct(video.watchPercentage)
                                            }
                                            // Seek player to saved position
                                            if (isExternalVideo(videoSrc)) {
                                              extAssessmentSeekTo(data.watchedSeconds)
                                            } else {
                                              pendingSeekRef.current = data.watchedSeconds
                                            }
                                          }
                                        } catch {
                                          // No prior progress — start from beginning
                                        }
                                      }
                                    }
                                  }}
                                >
                                  {/* Thumbnail */}
                                  <div className='relative aspect-video rounded-[12px] overflow-hidden mb-3'>
                                    {thumbSrc ? (
                                      <img src={thumbSrc} alt={video.title || ''} className='absolute inset-0 h-full w-full object-cover' />
                                    ) : (
                                      <div className='absolute inset-0 bg-gradient-to-br from-[#FFB366] via-[#FF9933] to-[#FF8000]' />
                                    )}

                                    {/* Completed overlay */}
                                    {video.isWatched && (
                                      <div className='absolute inset-0 bg-black/40 flex items-center justify-center z-10'>
                                        <div className='w-12 h-12 rounded-full bg-green-500 flex items-center justify-center'>
                                          <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
                                            <path d='M16.78 9.7L11.11 15.37C10.97 15.51 10.78 15.59 10.58 15.59C10.38 15.59 10.19 15.51 10.05 15.37L7.22 12.54C6.93 12.25 6.93 11.77 7.22 11.48C7.51 11.19 7.99 11.19 8.28 11.48L10.58 13.78L15.72 8.64C16.01 8.35 16.49 8.35 16.78 8.64C17.07 8.93 17.07 9.4 16.78 9.7Z' fill='white'/>
                                          </svg>
                                        </div>
                                      </div>
                                    )}

                                    {/* Play button */}
                                    {!video.isWatched && (
                                      <div className='absolute inset-0 flex items-center justify-center'>
                                        <div className='h-[48px] w-[48px] rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform'>
                                          <svg className='h-5 w-5 text-[#151515] ml-0.5' fill='currentColor' viewBox='0 0 20 20'>
                                            <path d='M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z' />
                                          </svg>
                                        </div>
                                      </div>
                                    )}

                                    {/* Duration badge */}
                                    <div className='absolute bottom-2 right-2 px-2 py-0.5 bg-[#151515]/80 text-white text-[12px] font-medium rounded-[4px] z-10'>
                                      {formatDur(video.duration)}
                                    </div>

                                    {/* Watch Progress Bar */}
                                    {!video.isWatched && video.watchPercentage != null && video.watchPercentage > 0 && (
                                      <div className='absolute bottom-0 left-0 right-0 h-1 bg-black/20 z-10'>
                                        <div
                                          className='h-full bg-[#FA6017] transition-all duration-300'
                                          style={{ width: `${Math.min(video.watchPercentage, 100)}%` }}
                                        />
                                      </div>
                                    )}
                                  </div>

                                  {/* Video Info */}
                                  <div className='flex flex-col gap-1'>
                                    <div className='flex items-center gap-2'>
                                      <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-[4px] gap-1 ${
                                        video.isWatched
                                          ? 'bg-green-600 text-white'
                                          : 'bg-[#151515] text-white'
                                      }`}>
                                        {video.isWatched ? (
                                          <>
                                            <svg width='12' height='12' viewBox='0 0 24 24' fill='none'>
                                              <path d='M16.78 9.7L11.11 15.37C10.97 15.51 10.78 15.59 10.58 15.59C10.38 15.59 10.19 15.51 10.05 15.37L7.22 12.54C6.93 12.25 6.93 11.77 7.22 11.48C7.51 11.19 7.99 11.19 8.28 11.48L10.58 13.78L15.72 8.64C16.01 8.35 16.49 8.35 16.78 8.64C17.07 8.93 17.07 9.4 16.78 9.7Z' fill='white'/>
                                            </svg>
                                            Completed
                                          </>
                                        ) : (
                                          <>
                                            <svg className='h-3 w-3' fill='currentColor' viewBox='0 0 20 20'>
                                              <path d='M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z' />
                                            </svg>
                                            Video {idx + 1}
                                          </>
                                        )}
                                      </span>
                                    </div>
                                    <h4 className={`text-[14px] font-medium leading-[20px] line-clamp-2 transition-colors ${
                                      video.isWatched
                                        ? 'text-green-700'
                                        : 'text-[#151515] group-hover:text-[#FA6017]'
                                    }`}>
                                      {video.title || `Video ${idx + 1}`}
                                    </h4>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Continue Button */}
                    <div className='flex justify-center'>
                      <Button
                        onClick={handleContinueToNextSection}
                        disabled={!allVideosCompleted}
                        className={`px-8 py-6 text-lg font-semibold rounded-xl ${
                          allVideosCompleted
                            ? 'bg-gray-900 hover:bg-gray-800 text-white'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        {allVideosCompleted ? 'Continue to Next Section' : 'Complete All Videos to Continue'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className='mb-6 text-start'>
                      <h2 className='mb-2 text-[20px] font-bold text-[#151515] leading-[32px]'>Section {currentSection}</h2>
                      <p className='text-[14px] font-normal text-[#747474] leading-[24px] max-w-[1187px]'>Answer the questions honestly; your responses are private and help tailor your guidance.</p>
                    </div>
                    <div className='space-y-6'>
                      {sectionQuestions.map((q, idx) => {
                        const opts = q.options?.map((o, i) => ({ id: o.optionText || `opt-${i}`, label: o.optionText || '', value: o.optionValue ?? 0 })) || []
                        return (
                          <AssessmentQuestion
                            key={q.id}
                            questionNumber={idx + 1}
                            questionText={q.questionText}
                            options={opts.length ? opts : []}
                            selectedValue={answers[q.id]}
                            onSelect={(val) => handleSelect(q.id, val)}
                          />
                        )
                      })}
                      {sectionQuestions.length === 0 && <p className='text-sm text-gray-600'>No questions found for this section.</p>}
                    </div>
                  </>
                )}
              </div>
              {assessment?.status !== 'completed' && !loading && !showScoreAnalysis && !showRequiredVideos && (
                <AssessmentPagination
                  currentPage={currentSection}
                  totalPages={totalSections}
                  onPageChange={() => {}}
                  onPrevious={() => {}}
                  onSave={handleSubmitSection}
                  nextLabel={submitting ? 'Submitting...' : 'Save & Continue'}
                  disableNext={submitting}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <AssessmentModal
        open={showModal}
        onOpenChange={setShowModal}
        type={modalType}
        onPrimaryAction={() => { if (modalType === 'review') { handleSubmitSection() } else { setShowModal(false) } }}
        skippedQuestionsCount={0}
      />
    </div>
  )
}
